import React, { useState, useEffect } from "react";
import { IoArrowBack, IoOpenOutline } from "react-icons/io5";
import { Eye, EyeOff, Check, X, Pencil } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { showStatusToast } from "../../components/ui/Toast";
import { PLATEFORME_ELECTRONIQUE_OPTIONS } from "../Portefeuille/plateformeElectroniqueOptions";
import PlateformeLogo from "../Portefeuille/PlateformeLogo";
import { useEtatGlobal } from "../EtatGlobal.js";

// Un seul jeu d'identifiants par PA pour tout le cabinet (pas par site,
// confirmé par Jonathan le 03/09/2026) — pré-rempli en proposition (jamais
// automatique/imposé) lorsqu'un compte "Solution de gestion / PA" est ajouté
// sur une fiche client (voir FicheClientOverlay.js).
//
// Consultation ouverte à tout collaborateur ; modification réservée aux
// administrateurs, sur ticket validé par Anthony ou Samuel (décision
// d'Anthony du 26/10/2026). Même règle côté backend.
function IdentifiantsPA({ onCancel }) {
  const { utilisateur } = useEtatGlobal();
  const peutModifier = utilisateur?.role === "ADMINISTRATEUR";
  const [identifiants, setIdentifiants] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingPlateforme, setEditingPlateforme] = useState(null);
  const [editingData, setEditingData] = useState({ identifiant: "", motDePasse: "" });
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/identifiants-pa-defaut"
      );
      const map = {};
      (data || []).forEach((row) => {
        map[row.plateforme] = row;
      });
      setIdentifiants(map);
    } catch (err) {
      console.error("Erreur chargement identifiants PA par défaut :", err);
      setLoadError(
        "Impossible de charger les identifiants PA par défaut. Réessayez, ou contactez le support si l'erreur persiste."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (plateforme) => {
    const existing = identifiants[plateforme];
    setEditingPlateforme(plateforme);
    setEditingData({
      identifiant: existing?.identifiant || "",
      motDePasse: existing?.motDePasse || "",
    });
  };

  const cancelEdit = () => {
    setEditingPlateforme(null);
    setEditingData({ identifiant: "", motDePasse: "" });
  };

  const handleSave = async (plateforme) => {
    setSaving(true);
    const toast = showStatusToast({
      status: "loading",
      titre: "Enregistrement en cours...",
      message: `Identifiant par défaut pour ${plateforme}.`,
    });
    try {
      const { data } = await httpClient.put(
        `https://backend-myalfa.vercel.app/api/identifiants-pa-defaut/${encodeURIComponent(plateforme)}`,
        {
          identifiant: editingData.identifiant,
          motDePasse: editingData.motDePasse,
        }
      );
      // Disponible tout de suite pour cette PA — la fiche client (onglet
      // Comptes) recharge cette même liste à chaque ouverture, donc la
      // proposition d'identifiant par défaut est à jour dès le prochain
      // client ouvert, sans rechargement de page.
      setIdentifiants((prev) => ({ ...prev, [plateforme]: data }));
      cancelEdit();
      toast.update({
        status: "success",
        titre: "Enregistré",
        message: `Identifiant par défaut mis à jour pour ${plateforme}.`,
      });
    } catch (err) {
      console.error("Erreur enregistrement identifiant PA par défaut :", err);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: `L'enregistrement pour ${plateforme} a échoué.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleShowPassword = (plateforme) =>
    setShowPasswords((prev) => ({ ...prev, [plateforme]: !prev[plateforme] }));

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-[#840040] transition-colors flex-shrink-0"
        >
          <IoArrowBack size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Identifiants PA</h1>
        <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
          {PLATEFORME_ELECTRONIQUE_OPTIONS.length}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-6 ml-8">
        Identifiants du cabinet par solution de gestion / PA — un seul jeu pour tout le cabinet,
        proposé (jamais imposé) lors de l'ajout d'un compte "Solution de gestion / PA" sur une
        fiche client.
      </p>
      {!peutModifier && (
        <div className="ml-8 mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          Consultation seule. Toute modification des identifiants PA doit faire l'objet d'un ticket,
          validé au préalable par Anthony ou Samuel.
        </div>
      )}

      {loadError && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
          <span>{loadError}</span>
          <button
            onClick={load}
            className="flex-shrink-0 text-xs font-semibold text-red-700 hover:text-red-900 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : loadError ? null : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#fff6ee] text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                <th className="px-5 py-3">PA</th>
                <th className="px-5 py-3">Identifiant</th>
                <th className="px-5 py-3">Mot de passe</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {PLATEFORME_ELECTRONIQUE_OPTIONS.map((plateforme) => {
                const existing = identifiants[plateforme];
                const isEditing = editingPlateforme === plateforme;

                if (isEditing) {
                  return (
                    <tr key={plateforme} className="bg-[#840040]/5">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <PlateformeLogo value={plateforme} className="h-5 w-auto max-w-[56px]" />
                          <span className="font-semibold text-gray-900">{plateforme}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <input
                          type="text"
                          autoFocus
                          value={editingData.identifiant}
                          onChange={(e) =>
                            setEditingData((p) => ({ ...p, identifiant: e.target.value }))
                          }
                          placeholder="Optionnel"
                          className="w-full text-xs border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040] font-mono"
                        />
                      </td>
                      <td className="px-5 py-4">
                        <input
                          type="text"
                          value={editingData.motDePasse}
                          onChange={(e) =>
                            setEditingData((p) => ({ ...p, motDePasse: e.target.value }))
                          }
                          placeholder="Optionnel"
                          className="w-full text-xs border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040] font-mono"
                        />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => handleSave(plateforme)}
                            disabled={saving}
                            className="flex items-center gap-1 text-[11px] text-white bg-[#5C1A30] hover:bg-[#4a1526] px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
                          >
                            <Check className="w-3 h-3" />
                            Enregistrer
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            title="Annuler"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={plateforme} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <PlateformeLogo value={plateforme} className="h-5 w-auto max-w-[56px]" />
                        <span className="font-semibold text-gray-900">{plateforme}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600 font-mono">
                      {existing?.identifiant || <span className="text-gray-300 font-sans">—</span>}
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {existing?.motDePasse ? (
                        <div className="flex items-center gap-1.5 font-mono">
                          <span>{showPasswords[plateforme] ? existing.motDePasse : "••••••"}</span>
                          <button
                            onClick={() => toggleShowPassword(plateforme)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPasswords[plateforme] ? (
                              <EyeOff className="w-3 h-3" />
                            ) : (
                              <Eye className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {peutModifier && (
                        <button
                          onClick={() => openEdit(plateforme)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-[#840040] hover:text-[#840040] transition-colors whitespace-nowrap"
                        >
                          {existing ? <IoOpenOutline size={13} /> : <Pencil className="w-3 h-3" />}
                          {existing ? "Ouvrir" : "Renseigner"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default IdentifiantsPA;
